import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  CompetitionPoster,
  DateField,
  Input,
  PosterImage,
  Textarea,
  compressImage,
} from '@structural-climbing/ui'
import { COMPETITION_SCORING_TYPES } from '@structural-climbing/validation'
import type { CompetitionFormat } from '../types'
import { OptionCards } from './OptionCards'
import { Segmented } from './Segmented'
import { EntryFeeBreakdown } from './EntryFeeBreakdown'
import type { CompetitionForm, EntryMode } from './useCompetitionForm'

/** Matches the Worker's own limit, so a rejected upload is caught here first. */
const MAX_POSTER_BYTES = 5 * 1024 * 1024

/** Card payments are built but not switched on yet; the option shows, greyed. */
const PAY_ONLINE_READY = false

function StepHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div>
      <h2 className="text-xl font-bold">{title}</h2>
      {hint && <p className="mt-1 text-sm text-foreground/60">{hint}</p>}
    </div>
  )
}

/** Step 1: what members read before they enter. */
export function AboutStep({ form, locked }: { form: CompetitionForm; locked?: boolean }) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const f = form.fieldErrors

  async function handlePosterPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    form.setError(null)
    try {
      const compressed = await compressImage(file, MAX_POSTER_BYTES)
      const token = await getAccessTokenSilently()
      const body = new FormData()
      body.append('file', compressed)
      const res = await fetch(`${apiUrl}/competition-poster`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })
      if (!res.ok) throw new Error()
      const { url } = (await res.json()) as { url: string }
      // Held in state only: it reaches the competition when the step is saved.
      form.setPosterUrl(url)
    } catch {
      form.setError(t('competitions.errorPoster'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <StepHeading
        title={t('competitionSetup.aboutTitle')}
        hint={t('competitionSetup.aboutHint')}
      />
      <Input
        label={t('competitions.fieldName')}
        value={form.name}
        onValueChange={form.setName}
        isInvalid={!!f.name}
        errorMessage={f.name}
        isRequired
        isDisabled={locked}
      />
      {/* A live preview, so a gym sees that leaving this alone still gives the
          competition a picture rather than a blank frame. */}
      <div className="flex items-center gap-4">
        <div className="h-32 w-24 shrink-0 overflow-hidden rounded-xl">
          {form.posterUrl ? (
            <PosterImage
              src={form.posterUrl}
              zoomable="image"
              labels={{
                open: t('competitions.posterOpen'),
                close: t('competitions.posterClose'),
                zoomHint: t('competitions.posterZoomHint'),
              }}
            />
          ) : (
            <CompetitionPoster
              name={form.name || t('competitionSetup.newTitle')}
              showName={false}
            />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold">{t('competitions.fieldPoster')}</p>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => void handlePosterPick(e)}
            />
            <Button
              size="sm"
              variant="flat"
              isLoading={uploading}
              isDisabled={locked}
              onPress={() => fileInputRef.current?.click()}
            >
              {form.posterUrl ? t('competitions.replacePoster') : t('competitions.uploadPoster')}
            </Button>
            {form.posterUrl && !locked && (
              <Button
                size="sm"
                variant="light"
                color="danger"
                onPress={() => form.setPosterUrl(null)}
              >
                {t('competitions.removePoster')}
              </Button>
            )}
          </div>
          <p className="text-xs text-foreground/60">{t('competitions.posterHint')}</p>
        </div>
      </div>
      <Textarea
        label={t('competitions.fieldDescription')}
        description={t('competitions.fieldDescriptionHint')}
        value={form.description}
        onValueChange={form.setDescription}
        isInvalid={!!f.description}
        errorMessage={f.description}
        minRows={3}
        isDisabled={locked}
      />
      <Textarea
        label={t('competitions.fieldPrizes')}
        description={t('competitions.fieldPrizesHint')}
        value={form.prizes}
        onValueChange={form.setPrizes}
        isInvalid={!!f.prizes}
        errorMessage={f.prizes}
        minRows={2}
        isDisabled={locked}
      />
    </div>
  )
}

/** Step 2: discipline, format, the window and how it is scored. */
export function WhenStep({ form, locked }: { form: CompetitionForm; locked?: boolean }) {
  const { t } = useTranslation()
  const f = form.fieldErrors
  const singleDay = form.format === 'single_day'

  return (
    <div className="flex flex-col gap-5">
      <StepHeading title={t('competitionSetup.whenTitle')} />

      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold">{t('competitions.fieldDiscipline')}</p>
        <Segmented
          label={t('competitions.fieldDiscipline')}
          value={form.discipline}
          onChange={form.setDiscipline}
          isDisabled={locked}
          options={form.disciplineOptions.map((d) => ({
            value: d,
            label: t(`competitions.discipline_${d}`),
          }))}
        />
        {form.discipline === 'combined' && (
          <p className="text-xs text-foreground/60">{t('competitions.disciplineHint_combined')}</p>
        )}
        {f.discipline && <p className="text-sm text-danger">{f.discipline}</p>}
      </div>

      <OptionCards<CompetitionFormat>
        label={t('competitions.fieldFormat')}
        value={form.format}
        onChange={form.setFormat}
        columns={2}
        options={(['single_day', 'long_running'] as const).map((value) => ({
          value,
          title: t(`competitions.format_${value}`),
          hint: t(`competitionSetup.formatHint_${value}`),
          isDisabled: locked,
        }))}
      />
      {f.format && <p className="-mt-3 text-sm text-danger">{f.format}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <DateField
          withTime
          label={t('competitions.fieldStartsAt')}
          value={form.startsAt}
          onValueChange={form.setStartsAt}
          isInvalid={!!f.starts_at}
          errorMessage={f.starts_at}
          isRequired
          isDisabled={locked}
        />
        <DateField
          withTime
          label={t('competitions.fieldEndsAt')}
          // A single-day competition cannot run past the day it starts, so the
          // picker will not offer another one.
          min={singleDay && form.startsAt ? form.startsAt : undefined}
          max={singleDay && form.startsAt ? `${form.startsAt.slice(0, 10)}T23:59` : undefined}
          value={form.endsAt}
          onValueChange={form.setEndsAt}
          isInvalid={!!f.ends_at}
          errorMessage={f.ends_at}
          isRequired
          isDisabled={locked}
        />
      </div>

      {/* Editable after the start on purpose: nothing is denormalised onto an
          ascent, so switching the formula just recomputes the board. */}
      <div className="flex flex-col gap-2">
        <OptionCards
          label={t('competitions.fieldScoring')}
          value={form.scoringType}
          onChange={form.setScoringType}
          columns={2}
          options={COMPETITION_SCORING_TYPES.map((s) => ({
            value: s,
            title: t(`competitions.scoring_${s}`),
            hint: t(`competitions.scoringHint_${s}`),
          }))}
        />
        {f.scoring_type && <p className="text-sm text-danger">{f.scoring_type}</p>}
        <p className="text-xs text-foreground/60">{t('competitionSetup.scoringChangeNote')}</p>
      </div>
    </div>
  )
}

/** The fee field pair, shared by the modes that show or take one. */
function FeeFields({
  form,
  label,
  hint,
  isRequired,
}: {
  form: CompetitionForm
  label: string
  hint?: string
  isRequired?: boolean
}) {
  const { t } = useTranslation()
  const f = form.fieldErrors
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Input
        type="number"
        min={0}
        step="0.01"
        label={label}
        description={hint}
        value={form.entryFee}
        onValueChange={form.setEntryFee}
        isInvalid={!!f.entry_fee_cents}
        errorMessage={f.entry_fee_cents}
        isRequired={isRequired}
        classNames={{ inputWrapper: 'bg-content1' }}
      />
      <Input
        label={t('competitions.fieldEntryFeeCurrency')}
        value={form.entryFeeCurrency}
        onValueChange={(v) => form.setEntryFeeCurrency(v.toUpperCase().slice(0, 3))}
        isInvalid={!!f.entry_fee_currency}
        errorMessage={f.entry_fee_currency}
        classNames={{ inputWrapper: 'bg-content1' }}
      />
    </div>
  )
}

/** Step 3: who may enter and how they get in. */
export function EntryStep({ form }: { form: CompetitionForm }) {
  const { t } = useTranslation()
  const f = form.fieldErrors
  const shownFee = (
    <FeeFields
      form={form}
      label={t('competitionSetup.feeShownLabel')}
      hint={t('competitionSetup.feeShownHint')}
    />
  )

  return (
    <div className="flex flex-col gap-5">
      <StepHeading title={t('competitionSetup.entryTitle')} />

      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold">{t('competitionSetup.joinQuestion')}</p>
        <Segmented
          label={t('competitionSetup.joinQuestion')}
          value={form.joinPolicy}
          onChange={form.setJoinPolicy}
          options={(['open', 'members_only'] as const).map((p) => ({
            value: p,
            label: t(`competitions.joinPolicy_${p}`),
          }))}
        />
        <p className="text-xs text-foreground/60">{t('competitions.joinPolicyHint')}</p>
        {f.join_policy && <p className="text-sm text-danger">{f.join_policy}</p>}
      </div>

      <OptionCards<EntryMode>
        label={t('competitionSetup.modeQuestion')}
        value={form.entryMode}
        onChange={form.setEntryMode}
        options={[
          {
            value: 'free',
            title: t('competitionSetup.mode_free'),
            hint: t('competitionSetup.modeHint_free'),
            children: shownFee,
          },
          {
            value: 'desk',
            title: t('competitionSetup.mode_desk'),
            hint: t('competitionSetup.modeHint_desk'),
            children: shownFee,
          },
          {
            value: 'online',
            title: t('competitionSetup.mode_online'),
            hint: PAY_ONLINE_READY
              ? t('competitionSetup.modeHint_online')
              : `${t('competitionSetup.modeHint_online')} ${t('competitionSetup.modeOnlineSoon')}`,
            isDisabled: !PAY_ONLINE_READY,
            children: (
              <div className="flex flex-col gap-3">
                <FeeFields form={form} label={t('competitions.fieldEntryFee')} isRequired />
                <EntryFeeBreakdown
                  fee={Number(form.entryFee) || 0}
                  currency={form.entryFeeCurrency || 'RON'}
                />
              </div>
            ),
          },
        ]}
      />
      {f.requires_confirmation && <p className="text-sm text-danger">{f.requires_confirmation}</p>}
    </div>
  )
}
